import EigenLeadsDiagramFrame from './EigenLeadsDiagramFrame';
import { eigenLeadsPalette, elType } from './eigenLeadsPalette';
import { ElDefs, ElGrid, ElSectionLabel, ElPanel, ElConnector } from './eigenLeadsDiagramKit';

const PREFIX = 'elQuality';

const contacts = [
  { id: 'c1', score: 0.94, rank: 1, intent: 'high' },
  { id: 'c2', score: 0.89, rank: 2, intent: 'med' },
  { id: 'c3', score: 0.86, rank: 3, intent: 'med' },
  { id: 'c4', score: 0.71, rank: '—', intent: 'hold' },
];

const checks = [
  { label: 'Reachability', status: 'pass' },
  { label: 'Coverage', status: 'pass' },
  { label: 'Compliance', status: 'pass' },
  { label: 'Score floor', status: 'pass' },
];

const EigenLeadsQualityDiagram = ({ theme = 'dark' }) => {
  const isDark = theme === 'dark';
  const c = eigenLeadsPalette(isDark);

  return (
    <EigenLeadsDiagramFrame
      theme={theme}
      caption="EigenScore ranking · QualityCert gate · contacts worth dialing"
      className="el-quality-frame"
    >
      <svg viewBox="0 0 720 400" className="w-full h-auto el-quality-svg" role="img" aria-label="EigenScore ranking and QualityCert certification">
        <ElDefs prefix={PREFIX} isDark={isDark} />
        <rect width="720" height="400" fill={c.bg} />
        <ElGrid width={720} height={400} c={c} />

        <ElSectionLabel x={360} y={36} c={c}>
          SCORE · CERTIFY · RELEASE
        </ElSectionLabel>

        <ElPanel x={48} y={64} w={280} h={300} prefix={PREFIX} c={c}>
          <text x={72} y={92} fill={c.indigo} {...elType.title}>
            INGEST BATCH
          </text>
          <text x={72} y={110} fill={c.mono} {...elType.monoSm}>
            batch_id · B-8842 · n=248
          </text>
          <line x1={72} y1={120} x2={304} y2={120} stroke={c.grid} strokeWidth="1" />
          {contacts.map(({ id, score, rank, intent }, i) => {
            const y = 136 + i * 52;
            const released = score >= 0.72;
            return (
              <g key={id} className={`el-quality-row el-quality-row-${i}`}>
                <rect x={72} y={y} width="232" height="40" rx="4" fill="none" stroke={c.panelStroke} strokeWidth="1" />
                <text x={84} y={y + 16} fill={c.mono} {...elType.monoSm}>
                  contact_{i + 1}
                </text>
                <text x={84} y={y + 30} fill={c.muted} {...elType.sub}>
                  intent:{intent}
                </text>
                <text x={280} y={y + 24} textAnchor="end" fill={released ? c.emerald : c.amber} {...elType.mono}>
                  {score.toFixed(2)}
                </text>
                <rect
                  x={180}
                  y={y + 28}
                  width={(80 * score)}
                  height="6"
                  rx="3"
                  fill={released ? c.emerald : c.amber}
                  opacity="0.55"
                  className={`el-quality-bar el-quality-bar-${i}`}
                />
              </g>
            );
          })}
        </ElPanel>

        <line x1={328} y1={200} x2={364} y2={200} stroke={c.indigo} strokeWidth="2" className="el-quality-flow" markerEnd={`url(#${PREFIX}-arrow)`} />
        <circle r="4" fill={c.indigo} filter={`url(#${PREFIX}-glow)`} className="el-quality-flow-dot">
          <animateMotion dur="2.5s" repeatCount="indefinite" path="M 328 200 L 364 200" />
        </circle>

        <ElPanel x={364} y={64} w={148} h={300} prefix={PREFIX} c={c} stroke={c.amber} className="el-quality-gate">
          <text x={388} y={92} fill={c.amber} {...elType.title}>
            QualityCert™
          </text>
          <text x={388} y={110} fill={c.muted} {...elType.sub}>
            Pre-release gate
          </text>
          <line x1={388} y1={120} x2={488} y2={120} stroke={c.grid} strokeWidth="1" />
          {checks.map(({ label, status }, i) => (
            <g key={label} className={`el-quality-check el-quality-check-${i}`}>
              <rect x={380} y={132 + i * 44} width="116" height="32" rx="4" fill="none" stroke={c.panelStroke} strokeWidth="1" />
              <text x={392} y={152 + i * 44} fill={c.label} {...elType.sub}>
                {label}
              </text>
              <text x={480} y={152 + i * 44} textAnchor="end" fill={c.emerald} {...elType.monoSm}>
                {status}
              </text>
            </g>
          ))}
          <text x={438} y={340} textAnchor="middle" fill={c.amber} {...elType.monoSm} className="el-quality-cert-badge">
            CERTIFIED
          </text>
        </ElPanel>

        <line x1={512} y1={200} x2={548} y2={200} stroke={c.emerald} strokeWidth="2" className="el-quality-release-flow" markerEnd={`url(#${PREFIX}-arrow)`} />

        <ElPanel x={548} y={64} w={124} h={300} prefix={PREFIX} c={c} active stroke={c.emerald}>
          <text x={572} y={92} fill={c.emerald} {...elType.title}>
            QUEUE
          </text>
          <text x={572} y={110} fill={c.mono} {...elType.monoSm}>
            ranked · ready
          </text>
          {[1, 2, 3].map((rank, i) => (
            <g key={rank} className={`el-quality-queue el-quality-queue-${i}`}>
              <text x={572} y={140 + i * 36} fill={c.label} {...elType.mono}>
                #{rank}
              </text>
              <text x={572} y={154 + i * 36} fill={c.muted} {...elType.monoSm}>
                score:0.{94 - i * 5}
              </text>
              <line x1={572} y1={162 + i * 36} x2={652} y2={162 + i * 36} stroke={c.grid} strokeWidth="1" />
            </g>
          ))}
          <text x={610} y={340} textAnchor="middle" fill={c.emerald} {...elType.monoSm}>
            worth dialing
          </text>
        </ElPanel>

        <ElPanel x={48} y={376} w={624} h={20} prefix={PREFIX} c={c}>
          <text x={72} y={390} fill={c.mono} {...elType.monoSm}>
            EigenScore™ rationale attached · every record ranked before release
          </text>
        </ElPanel>
      </svg>
    </EigenLeadsDiagramFrame>
  );
};

export default EigenLeadsQualityDiagram;
